import type { ReactNode } from "react";
import {
    DeviceActivity,
    Resources,
    Groups,
    LinkedAccounts,
    Oid4Vci,
    SigningIn,
} from "@keycloak/keycloak-account-ui";
import type { TFuncKey } from "./i18n.ts";
import type { Features } from "./environment.ts";
import { WelcomePage } from "./content/account/WelcomePage.tsx";
import { PersonalInfoPage } from "./content/account/PersonalInfoPage.tsx";
import { DeleteMyAccountPage } from "./content/account/DeleteMyAccountPage.tsx";
import { ContactUsPage } from "./content/association/ContactUsPage.tsx";
import { GeneralMeetingsPage } from "./content/association/GeneralMeetingsPage.tsx";
import { OverviewPage } from "./content/donations/OverviewPage.tsx";
import { TaxReceiptsPage } from "./content/donations/TaxReceiptsPage.tsx";
import { RecurringCommitmentsPage } from "./content/donations/RecurringCommitmentsPage.tsx";

type RootMenuItem = {
    index?: boolean,
    id?: string;
    label: TFuncKey;
    path: string;
    isVisible?: keyof Features;
    element: ReactNode
};
type MenuItemWithChildren = {
    label: TFuncKey;
    children: MenuItem[];
    isVisible?: keyof Features;
};
export type MenuItem = RootMenuItem | MenuItemWithChildren;

export const SigningInRoute: RootMenuItem = {
    label: "signingIn",
    path: "account-security/signing-in",
    element: <SigningIn />,
};

export const DeviceActivityRoute: RootMenuItem = {
    label: "deviceActivity",
    path: "account-security/device-activity",
    element: <DeviceActivity />,
};

export const LinkedAccountsRoute: RootMenuItem = {
    label: "linkedAccounts",
    path: "account-security/linked-accounts",
    element: <LinkedAccounts />,
    isVisible: "isLinkedAccountsEnabled",
};

export const AccountSecurity: MenuItemWithChildren = {
    label: "accountSecuritySidebarTitle",
    children: [
        SigningInRoute,
        DeviceActivityRoute,
        LinkedAccountsRoute,
    ],
};

export const GroupsRoute: RootMenuItem = {
    label: "groups",
    path: "groups",
    element: <Groups />,
    isVisible: "isViewGroupsEnabled",
};

export const ResourcesRoute: RootMenuItem = {
    label: "resources",
    path: "resources",
    element: <Resources />,
    isVisible: "isMyResourcesEnabled",
};

export const Oid4VciRoute: RootMenuItem = {
    label: "oid4vci",
    path: "oid4vci",
    element: <Oid4Vci />,
    isVisible: "isOid4VciEnabled",
};

export const WelcomeRoute: RootMenuItem = {
    index: true,
    label: 'welcomeTitle',
    path: "",
    element: <WelcomePage />
};

export const PersonalInfoRoute: RootMenuItem = {
    label: "personalInfo",
    path: "details",
    element: <PersonalInfoPage />,
};

export const DeleteMyAccountRoute: RootMenuItem = {
    label: "deleteMyAccountTitle",
    path: "delete",
    element: <DeleteMyAccountPage />,
}

export const OverviewRoute: RootMenuItem = {
    label: "overview",
    path: "donations/overview",
    element: <OverviewPage />,
}

export const RecurringCommitmentsRoute: RootMenuItem = {
    label: "recurringCommitments",
    path: "donations/recurring-commitments",
    element: <RecurringCommitmentsPage />,
}

export const TaxReceiptsRoute: RootMenuItem = {
    label: "taxReceipts",
    path: "donations/tax-receipts",
    element: <TaxReceiptsPage />,
}

export const GeneralMeetingsRoute: RootMenuItem = {
    label: "organizationTitle",
    path: "general-meetings",
    element: <GeneralMeetingsPage />,
}

export const ContactUsRoute: RootMenuItem = {
    label: "contactUsTitle",
    path: "contact",
    element: <ContactUsPage />,
}

export const ContactUs: MenuItemWithChildren = {
    label: "contactUsTitle",
    children: [
        ContactUsRoute,
        DeleteMyAccountRoute,
    ],
}

export const Donations: MenuItemWithChildren = {
    label: "donationsTitle",
    children: [
        OverviewRoute,
        RecurringCommitmentsRoute,
        TaxReceiptsRoute,
    ],
}

export const routes: MenuItem[] = [
    WelcomeRoute,
    PersonalInfoRoute,
    Donations,
    GeneralMeetingsRoute,
    AccountSecurity,
    GroupsRoute,
    ResourcesRoute,
    Oid4VciRoute,
    ContactUs,
];
